import { format, parse } from "date-fns";

import {
  BOOKING_SUBSCRIPTIONS_ENDPOINT,
  CONTRACT_CANCELLATION,
  CONTRACT_CANCELLATION_REASONS,
  PROFILE_ACCOUNT_BALANCE_ENDPOINT,
  PROFILE_BASIC_INFO_ENDPOINT,
  PROFILE_COMPANY_INFO_ENDPOINT,
  PROFILE_CONTRACTS,
  PROFILE_DEFAULT_PAYMENT_ALIAS_ENDPOINT,
  PROFILE_DELETE_PAYMENT_ALIAS_ENDPOINT,
  PROFILE_PASSWORD_ENDPOINT,
  PROFILE_PAYMENT_ALIASES_ENDPOINT,
  REFERRAL_ENDPOINT,
  VOUCHER_ENDPOINT,
  REVIEW_INFO_ENDPOINT,
} from "@config/endpoints";

import { ContractCancellationDetails } from "@typings/contractCancellation";
import { BasicInfo, CompanyInfo } from "@typings/profile";
import { ThunkActionDispatch } from "@typings/reduxThunkActions";

import { AsyncActionResult } from "@services/AsyncActionCreatorFactory";
import { buildUrl } from "@services/BuildUrl";
import { DATE_FORMAT } from "@services/DateFormatter";
import { ASYNC_ACTION_TYPES } from "@services/FetchFacade";
import { nullifyString } from "@services/NullifyString";
import { ReduxActionNameCreator } from "@services/ReduxActionNameCreator";

import { asyncActionCreator } from "@app/services/AsyncActionCreator";
import { genericPendingAction } from "@app/services/GenericPendingAction";

const action = new ReduxActionNameCreator("profile");

export const FETCH_REFERRAL = action.make("fetch referral");
export const FETCH_ACCOUNT_BALANCE = action.make("fetch account balance");
export const FETCH_BASIC_INFO = action.make("fetch basic info");
export const SET_BASIC_INFO = action.make("set basic info");
export const FETCH_PAYMENT_ALIASES = action.make("fetch payment aliases");
export const DELETE_PAYMENT_ALIAS = action.make("delete payment alias");
export const ADD_PAYMENT_ALIAS = action.make("add payment alias");
export const SET_NEW_PASSWORD = action.make("set new password");
export const SET_DEFAULT_PAYMENT_ALIAS = action.make(
  "set default payment alias",
);
export const FETCH_SUBSCRIPTIONS = action.make("fetch subscriptions");
export const FETCH_CONTRACTS = action.make("fetch contracts");
export const FETCH_COMPANY_INFO = action.make("fetch company info");
export const SET_COMPANY_INFO = action.make("set company info");
export const FETCH_CANCELLATION_REASONS = action.make(
  "fetch cancellation reasons",
);
export const SUBMIT_CONTRACT_CANCELLATION = action.make(
  "submit contract cancellation",
);
export const ADD_VOUCHER = action.make("add voucher");
export const FETCH_REVIEW_INFO = action.make("fetch review info");

export const fetchAccountBalance =
  () =>
  async (dispatch: ThunkActionDispatch): Promise<AsyncActionResult> => {
    dispatch(genericPendingAction(FETCH_ACCOUNT_BALANCE));

    const action = await asyncActionCreator({
      url: PROFILE_ACCOUNT_BALANCE_ENDPOINT,
      action: FETCH_ACCOUNT_BALANCE,
    });

    return dispatch(action);
  };

export const fetchReferral =
  () =>
  async (dispatch: ThunkActionDispatch): Promise<AsyncActionResult> => {
    dispatch(genericPendingAction(FETCH_REFERRAL));

    const action = await asyncActionCreator({
      url: REFERRAL_ENDPOINT,
      action: FETCH_REFERRAL,
    });

    return dispatch(action);
  };

export const fetchPaymentAliases =
  () =>
  async (dispatch: ThunkActionDispatch): Promise<AsyncActionResult> => {
    dispatch(genericPendingAction(FETCH_PAYMENT_ALIASES));

    const action = await asyncActionCreator({
      url: PROFILE_PAYMENT_ALIASES_ENDPOINT,
      action: FETCH_PAYMENT_ALIASES,
    });

    return dispatch(action);
  };

export const deletePaymentAlias =
  (paymentAliasUuid: string) =>
  async (dispatch: ThunkActionDispatch): Promise<AsyncActionResult> => {
    const action = await asyncActionCreator({
      url: PROFILE_DELETE_PAYMENT_ALIAS_ENDPOINT.replace(
        "{uuid}",
        paymentAliasUuid,
      ),
      action: DELETE_PAYMENT_ALIAS,
      method: ASYNC_ACTION_TYPES.DELETE,
      params: { paymentAliasUuid },
    });

    return dispatch(action);
  };

export const addPaymentAlias =
  (payload?: { returnUrl?: string }) =>
  async (dispatch: ThunkActionDispatch): Promise<AsyncActionResult> => {
    const action = await asyncActionCreator({
      url: PROFILE_PAYMENT_ALIASES_ENDPOINT,
      action: ADD_PAYMENT_ALIAS,
      method: ASYNC_ACTION_TYPES.POST,
      body: {
        ...(payload?.returnUrl && { returnUrl: payload.returnUrl }),
      },
    });

    return dispatch(action);
  };

export const setDefaultPaymentAlias =
  (paymentAliasUuid: string) =>
  async (dispatch: ThunkActionDispatch): Promise<AsyncActionResult> => {
    const action = await asyncActionCreator({
      url: PROFILE_DEFAULT_PAYMENT_ALIAS_ENDPOINT,
      action: SET_DEFAULT_PAYMENT_ALIAS,
      method: ASYNC_ACTION_TYPES.PUT,
      body: { paymentAliasUuid },
      params: { paymentAliasUuid },
    });

    return dispatch(action);
  };

export const fetchBasicInfo =
  () =>
  async (
    dispatch: ThunkActionDispatch,
  ): Promise<AsyncActionResult<BasicInfo>> => {
    const action = await asyncActionCreator({
      url: PROFILE_BASIC_INFO_ENDPOINT,
      action: FETCH_BASIC_INFO,
    });

    return dispatch(action);
  };

export const setBasicInfo =
  (payload: Omit<BasicInfo, "oAuthProvider">) =>
  async (dispatch: ThunkActionDispatch): Promise<AsyncActionResult> => {
    const dateOfBirth = payload.dateOfBirth
      ? format(
          parse(payload.dateOfBirth, DATE_FORMAT.DAY_PICKER_INPUT, new Date()),
          DATE_FORMAT.YEAR_MONTH_DAY,
        )
      : null;

    const data = {
      ...payload,
      dateOfBirth,
      mobilePhone: nullifyString(payload.mobilePhone),
      homePhone: nullifyString(payload.homePhone),
      serviceVoucher:
        typeof payload.serviceVoucher === "string"
          ? nullifyString(payload.serviceVoucher)
          : payload.serviceVoucher,
    };

    const action = await asyncActionCreator({
      url: PROFILE_BASIC_INFO_ENDPOINT,
      action: SET_BASIC_INFO,
      method: ASYNC_ACTION_TYPES.PUT,
      body: data,
      params: data,
    });

    return dispatch(action);
  };

export const fetchCompanyInfo =
  () =>
  async (dispatch: ThunkActionDispatch): Promise<AsyncActionResult> => {
    const action = await asyncActionCreator({
      url: PROFILE_COMPANY_INFO_ENDPOINT,
      action: FETCH_COMPANY_INFO,
    });

    return dispatch(action);
  };

export const setCompanyInfo =
  (payload: CompanyInfo) =>
  async (dispatch: ThunkActionDispatch): Promise<AsyncActionResult> => {
    const action = await asyncActionCreator({
      url: PROFILE_COMPANY_INFO_ENDPOINT,
      action: SET_COMPANY_INFO,
      method: ASYNC_ACTION_TYPES.PUT,
      body: payload,
      params: payload,
    });

    return dispatch(action);
  };

export const setNewPassword =
  (currentPassword: string, newPassword: string) =>
  async (dispatch: ThunkActionDispatch): Promise<AsyncActionResult> => {
    const action = await asyncActionCreator({
      url: PROFILE_PASSWORD_ENDPOINT,
      action: SET_NEW_PASSWORD,
      method: ASYNC_ACTION_TYPES.PUT,
      body: { currentPassword, newPassword },
    });

    return dispatch(action);
  };

export const fetchSubscriptions =
  () =>
  async (dispatch: ThunkActionDispatch): Promise<AsyncActionResult> => {
    const action = await asyncActionCreator({
      url: BOOKING_SUBSCRIPTIONS_ENDPOINT,
      action: FETCH_SUBSCRIPTIONS,
    });

    return dispatch(action);
  };

export const fetchContracts =
  () =>
  async (dispatch: ThunkActionDispatch): Promise<AsyncActionResult> => {
    const action = await asyncActionCreator({
      url: PROFILE_CONTRACTS,
      action: FETCH_CONTRACTS,
    });

    return dispatch(action);
  };

export const fetchContractCancellationReasons =
  (contractUuid: string) =>
  async (dispatch: ThunkActionDispatch): Promise<AsyncActionResult> => {
    const action = await asyncActionCreator({
      url: buildUrl(CONTRACT_CANCELLATION_REASONS)
        .setPath("contractUuid", contractUuid)
        .done(),
      action: FETCH_CANCELLATION_REASONS,
      params: {
        contractUuid,
      },
    });

    return dispatch(action);
  };

export const submitContractCancellation =
  (contractUuid: string, details: ContractCancellationDetails) =>
  async (dispatch: ThunkActionDispatch): Promise<AsyncActionResult> => {
    const action = await asyncActionCreator({
      method: ASYNC_ACTION_TYPES.POST,
      url: buildUrl(CONTRACT_CANCELLATION)
        .setPath("contractUuid", contractUuid)
        .done(),
      action: SUBMIT_CONTRACT_CANCELLATION,
      body: {
        reasonId: details.reasonId,
        comment: details.comment,
        relocationCountry: details.relocationCountry,
        relocationCity: details.relocationCity,
      },
    });

    return dispatch(action);
  };

export const addVoucher =
  (token: string) =>
  async (dispatch: ThunkActionDispatch): Promise<AsyncActionResult> => {
    const action = await asyncActionCreator({
      url: VOUCHER_ENDPOINT,
      action: ADD_VOUCHER,
      method: ASYNC_ACTION_TYPES.POST,
      body: { token },
    });

    return dispatch(action);
  };

export const fetchReviewInfo =
  () =>
  async (dispatch: ThunkActionDispatch): Promise<AsyncActionResult> => {
    genericPendingAction(FETCH_REVIEW_INFO);

    const action = await asyncActionCreator({
      action: FETCH_REVIEW_INFO,
      url: REVIEW_INFO_ENDPOINT,
      method: ASYNC_ACTION_TYPES.GET,
    });

    return dispatch(action);
  };
